import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  updateDoc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

export type ReportCadence = 'weekly' | 'biweekly' | 'monthly';

export interface TeamReportConfig {
  id?: string;
  teamId: string;
  enabled: boolean;
  cadence: ReportCadence;
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  deliveryTime: string; // HH:MM format
  delivery: {
    email: boolean;
    slack: boolean;
    slackDM: boolean; // DM to team manager instead of channel
  };
  lookbackDays: number; // how far back to look for activity (7, 14, 30)
  createdAt?: Timestamp | ReturnType<typeof serverTimestamp>;
  updatedAt?: Timestamp | ReturnType<typeof serverTimestamp>;
}

const DEFAULT_CONFIG: Omit<TeamReportConfig, 'id' | 'teamId' | 'createdAt' | 'updatedAt'> = {
  enabled: true,
  cadence: 'weekly',
  dayOfWeek: 1, // Monday
  deliveryTime: '09:00',
  delivery: {
    email: true,
    slack: true,
    slackDM: true,
  },
  lookbackDays: 7,
};

export function getDefaultReportConfig(): typeof DEFAULT_CONFIG {
  return { ...DEFAULT_CONFIG, delivery: { ...DEFAULT_CONFIG.delivery } };
}

export async function createTeamReportConfig(
  orgId: string,
  teamId: string,
  config: Partial<Omit<TeamReportConfig, 'id' | 'createdAt' | 'updatedAt'>>
) {
  const configRef = doc(collection(db, 'orgs', orgId, 'teamReportConfigs'));

  const fullConfig = {
    ...DEFAULT_CONFIG,
    ...config,
    delivery: {
      ...DEFAULT_CONFIG.delivery,
      ...(config.delivery || {}),
    },
    teamId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(configRef, fullConfig);
  return configRef.id;
}

export async function getTeamReportConfig(orgId: string, teamId: string): Promise<TeamReportConfig | null> {
  try {
    const configsQuery = query(
      collection(db, 'orgs', orgId, 'teamReportConfigs'),
      where('teamId', '==', teamId)
    );

    const snapshot = await getDocs(configsQuery);

    if (snapshot.empty) {
      return null;
    }

    const docSnap = snapshot.docs[0];
    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as TeamReportConfig;
  } catch (error) {
    // Collection may not exist yet or rules not deployed
    console.warn('Could not load team report config:', error);
    return null;
  }
}

export async function updateTeamReportConfig(
  orgId: string,
  configId: string,
  updates: Partial<TeamReportConfig>
) {
  const configRef = doc(db, 'orgs', orgId, 'teamReportConfigs', configId);

  // Deep clean undefined values
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined && key !== 'id') {
      cleaned[key] = value;
    }
  }

  await updateDoc(configRef, {
    ...cleaned,
    updatedAt: serverTimestamp(),
  });
}

export function getCadenceLabel(cadence: ReportCadence): string {
  switch (cadence) {
    case 'weekly':
      return 'Weekly';
    case 'biweekly':
      return 'Bi-weekly';
    case 'monthly':
      return 'Monthly';
    default:
      return 'Weekly';
  }
}

export function getDayLabel(day: number): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[day] || 'Monday';
}

export function getLookbackLabel(days: number): string {
  switch (days) {
    case 7:
      return 'Last 7 days';
    case 14:
      return 'Last 14 days';
    case 30:
      return 'Last 30 days';
    default:
      return `Last ${days} days`;
  }
}
